// TANDA AS-5 — el handler de los botones nativos de Apple y Google, uno solo para las
// tres superficies (login, signup y la conversión de invitado del Panel).
//
// Existe para que el JSX de la web no se toque: los botones nativos son hermanos
// gateados de los web, y todo lo que comparten —el import dinámico, el estado de carga,
// el silencio ante la cancelación, el toast de error y el de bienvenida— vive aquí en vez
// de estar copiado tres veces.
//
// EL GATE VA ANTES DEL `import()`: en el build web `entrar()` se va en la primera línea,
// así que el navegador jamás pide ni el chunk de `lib/nativeSocialLogin` ni, dentro de
// él, el del plugin nativo. Mismo criterio que `purchases.js` (patrón tokenStore).

import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
// `useReadyTranslation` y no `useTranslation` a secas: es el envoltorio propio del repo
// (maneja la ventana de carga fría del namespace) y es el que ya usan las tres pantallas
// que montan este hook. Da igual para el runtime, pero NO da igual para el Panel: sus 19
// suites mockean el envoltorio, no `react-i18next`, así que un hook que se lo salte
// revienta el render entero del Dashboard en pruebas.
import { useReadyTranslation } from './useReadyTranslation';
import { useAuth } from '../contexts/AuthContext';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { formatDateLong } from '../lib/dateFormat';

export function useNativeSocialLogin({ onSuccess } = {}) {
  const { handleNativeSocialLogin } = useAuth();
  const { t } = useReadyTranslation('auth');
  // Cuál proveedor está en vuelo, o null. Sirve de doble candado: pinta el estado de
  // carga y bloquea un segundo toque (los botones web no lo necesitan porque su
  // `window.location.href` se lleva la página por delante; una hoja nativa no).
  const [pendiente, setPendiente] = useState(null);
  // estilos-1 (Bloque E, apunte 1): el numero de vuelo. `soltar` apaga la ruedita de una hoja que se quedo colgada
  // (la pantalla lo llama al regresar: `useSoltarAlRegresar`) y deja tocar otra vez; si la llamada colgada contesta
  // DESPUES, su `finally` ya no es el vuelo vigente y no le apaga la ruedita a la nueva. Lo que conteste (un login que
  // si termino) se sigue atendiendo igual que hoy.
  const vuelo = useRef(0);
  const soltar = useCallback(() => {
    vuelo.current += 1;
    setPendiente(null);
  }, []);

  const entrar = useCallback(async (provider) => {
    if (!IS_APP_BUILD) return;
    if (pendiente) return;
    vuelo.current += 1;
    const esteVuelo = vuelo.current;
    setPendiente(provider);
    try {
      const mod = await import('../lib/nativeSocialLogin');
      const data = provider === 'apple'
        ? await mod.loginWithAppleNative()
        : await mod.loginWithGoogleNative();
      // Cancelación del usuario = silencio absoluto (doctrina AS-3/AS-4): cerrar la hoja
      // es una decisión, no un error. El módulo devuelve null y aquí no pasa nada.
      if (!data) return;
      const sesion = await handleNativeSocialLogin(data, provider);
      // Mismos verbatims ratificados en AS-4 para las páginas de retorno web: el toast de
      // bienvenida no estrena texto por ser nativo.
      const bloque = provider === 'apple' ? 'apple_callback' : 'auth_callback';
      toast.success(sesion.user.name
        ? t(`${bloque}.welcome_template`, { name: sesion.user.name })
        : t(`${bloque}.welcome_simple`));
      if (onSuccess) onSuccess(sesion);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === 'email_recently_deleted') {
        toast.error(t('blocked.recently_deleted', {
          date: formatDateLong(detail.blocked_until),
        }), { duration: 10000 });
      } else if (detail?.code === 'google_email_unverified') {
        toast.error(t('blocked.google_email_unverified'));
      } else {
        // El fallback es el mismo de la página de retorno web del proveedor.
        toast.error(provider === 'apple'
          ? t('apple_callback.toasts.apple_failed_fallback')
          : t('auth_callback.toasts.google_failed_fallback'));
      }
    } finally {
      if (vuelo.current === esteVuelo) setPendiente(null);
    }
  }, [pendiente, handleNativeSocialLogin, onSuccess, t]);

  const entrarConApple = useCallback(() => { entrar('apple'); }, [entrar]);
  const entrarConGoogle = useCallback(() => { entrar('google'); }, [entrar]);

  return { pendiente, entrarConApple, entrarConGoogle, soltar };
}
